import { Component } from '@theme/component';

/**
 * @typedef {object} AjaxEmailSignupRefs
 * @property {HTMLButtonElement} submitButton
 * @property {HTMLElement} messageContainer
 */

class AjaxEmailSignup extends Component {
  /** @type {string[]} */
  requiredRefs = ['submitButton', 'messageContainer'];

  async #fetchWithTimeout(url, options = {}, timeout = 10000) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      return await fetch(url, {
        ...options,
        signal: controller.signal,
        credentials: 'same-origin',
      });
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Handles form submission via AJAX.
   * @param {SubmitEvent} event
   */
  onSubmit(event) {
    event.preventDefault();

    const form = this.querySelector('form');
    if (!form) return;

    const { submitButton, messageContainer } = /** @type {AjaxEmailSignupRefs} */ (this.refs);
    const formData = new FormData(form);
    const action = form.getAttribute('action') || '/contact';

    // Disable the button to prevent multiple submissions
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.classList.add('loading');
    }

    // Clear previous messages
    if (messageContainer) {
      messageContainer.textContent = '';
      messageContainer.hidden = true;
    }

    this.#fetchWithTimeout(action, {
      method: 'POST',
      body: new URLSearchParams(/** @type {any} */(formData)),
      headers: {
        'Accept': 'text/html',
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    })
    .then(async (response) => {
      const htmlText = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlText, 'text/html');

      const successSelector = `#Email-signup__message-success-${this.dataset.blockId}`;
      const errorSelector = `#Email-signup__message-error-${this.dataset.blockId}`;
      
      const successMessage = doc.querySelector(successSelector);
      const errorMessage = doc.querySelector(errorSelector);

      if (successMessage) {
        this.#showMessage(successMessage.innerHTML, 'success');
        form.reset();
      } else if (errorMessage) {
        this.#showMessage(errorMessage.innerHTML, 'error');
      } else {
        if (response.ok && (htmlText.includes('posted_successfully') || response.url.includes('customer_posted=true'))) {
           this.#showMessage('Thank you for signing up!', 'success');
           form.reset();
        } else {
           this.#showMessage('Something went wrong. Please try again.', 'error');
        }
      }
    })
    .catch((error) => {
      console.error('AJAX Email Signup Error:', error);
      this.#showMessage('Network error. Please try again.', 'error');
    })
    .finally(() => {
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.classList.remove('loading');
      }
    });
  }

  /**
   * Displays a message in the message container.
   * @param {string} html
   * @param {'success' | 'error'} type
   */
  #showMessage(html, type) {
    const { messageContainer } = /** @type {AjaxEmailSignupRefs} */ (this.refs);
    if (!messageContainer) return;

    messageContainer.innerHTML = html;
    messageContainer.hidden = false;
    messageContainer.className = `email-signup__message email-signup__message--${type}`;
    
    // Focus the message for accessibility
    messageContainer.focus();
  }
}

if (!customElements.get('ajax-email-signup')) {
  customElements.define('ajax-email-signup', AjaxEmailSignup);
}
